import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { ActivityTypeEnum } from '@olv/ui-shared/services/account.service';
import { ReportsService, UserActivityParams } from '@olv/ui-shared/services/reports.service';
import { DatePickerComponent } from '@olv/ui-shared/ui-shared/date-picker/date-picker.component';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { formatDateStandard } from '@olv/ui-shared/utils/dateFormat.utils';
import { DatePickerService, PresetPeriod, RangeOfDate } from '@olv/ui-shared/services/date-picker.service';
import { ShowMoreComponent } from '@olv/ui-shared/ui-shared/show-more/show-more.component';
import { enumToFilterFields } from '@olv/ui-shared/utils/enum-to-filter.util';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { MatDialog } from '@angular/material/dialog';
import { FiltersDialogComponent } from '@olv/ui-shared/ui-shared/filters-dialog/filters.component';
import { ExportComponent, ExportDialogData } from '@olv/ui-shared/ui-shared/export/export.component';
import { EmptyStateComponent } from '@olv/ui-shared/ui-shared/empty-state/empty-state.component';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { FailedLoginDrawerComponent } from './failed-login-drawer/failed-login-drawer.component';
import { FailedLoginInfo } from '@olv/ui-shared/services/reports.service';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
export interface UserActivityLog {
  activity: string;
  label: string;
  user: string;
  date: Date;
  failedLoginInfo?: FailedLoginInfo;
}

@Component({
  selector: 'app-reports-user-activity',
  standalone: true,
  imports: [
    MatIconModule,
    PillSearchComponent,
    HasPermissionDirective,
    DatePickerComponent,
    ShowMoreComponent,
    LoadingSpinnerComponent,
    EmptyStateComponent,
    FailedLoginDrawerComponent,
    ButtonComponent
],
  templateUrl: './reports-user-activity.component.html',
  styleUrl: './reports-user-activity.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ReportsUserActivityComponent implements OnInit {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  protected readonly formatDateStandard = formatDateStandard;

  private reportsService = inject(ReportsService);
  private datePickerService = inject(DatePickerService);
  private dialog = inject(MatDialog);
  private merchantService = inject(MerchantService);

  searchText = '';
  userActivities: UserActivityLog[] = [];
  selectedActivity: UserActivityLog | null = null;
  initialLoading = false;
  error: string | null = null;
  filterMap: UserActivityParams = {};
  filtersApplied = false;
  filters = [
    {
      name: 'Activity Type',
      filterFields: enumToFilterFields(ActivityTypeEnum),
    },
  ];
  pageSize = 10;
  skipCount = 0;
  totalCount = 0;
  isLoading = false;
  hasExportData = false;
  isDisableFilters = false;
  isFirstLoad = true;

  selectedPeriod = this.datePickerService.DEFAULT_DATE_PERIOD;

  ngOnInit() {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    const { start, end } = this.datePickerService.setDefaultDate();
    this.filterMap.startDate = start.toISOString();
    this.filterMap.endDate = end.toISOString();

    this.loadUserActivities();
  }

  loadUserActivities(loadMore = false): void {
    if (loadMore) {
      this.isLoading = true;
    } else {
      this.initialLoading = true;
      if (this.selectedActivity) {
        this.closeActivityDetails();
      }
      this.selectedActivity = null;
    }
    this.error = null;
    const filterPayload: UserActivityParams = {
      ...this.filterMap,
      filter: this.searchText?.trim() || undefined,
      skipCount: this.skipCount,
      maxResultCount: this.pageSize,
    };
    this.reportsService.getUserActivityLogs(filterPayload).subscribe({
      next: (res) => {
        const newItem = res.items.map((item) => ({
          activity: item.activityType,
          label: item.entityNameValue ?? 'N/A',
          user: item.keycloakUserName ?? 'N/A',
          date: new Date(item.date),
          failedLoginInfo: item.failedLoginInfo,
        }));
        this.userActivities = loadMore ? [...this.userActivities, ...newItem] : newItem;
        this.totalCount = res.totalCount;
        this.initialLoading = false;
        this.isLoading = false;
        this.hasExportData = this.userActivities.length > 0;

        if (this.isFirstLoad) {
          this.isDisableFilters = this.userActivities.length === 0 && !this.searchText?.trim();
        }
        this.isFirstLoad = false;
      },
      error: () => {
        this.error = 'Failed to load user activities.';
        this.initialLoading = false;
        this.isLoading = false;
        this.hasExportData = false;
        this.resetPagination();
      },
    });
  }

  onRowActivate(event: Event, userActivity: UserActivityLog): void {
    if (!userActivity.failedLoginInfo || event.target !== event.currentTarget) {
      return;
    }

    if (event instanceof KeyboardEvent && event.key === ' ') {
      event.preventDefault();
    }

    this.onActivityClick(userActivity);
  }

  onActivityClick(activity: UserActivityLog): void {
    if (!activity.failedLoginInfo) {
      this.closeActivityDetails();
      return;
    }

    if (this.isSelectedActivity(activity)) {
      this.closeActivityDetails();
      return;
    }

    this.selectedActivity = activity;
    this.merchantService.openSidebar();
  }

  isSelectedActivity(activity: UserActivityLog): boolean {
    return this.selectedActivity === activity;
  }

  closeActivityDetails(): void {
    this.selectedActivity = null;
    this.merchantService.closeSidebar();
  }

  loadMoreActivities(): void {
    if (this.isLoading || !this.hasMoreUserActivities) return;

    this.skipCount += this.pageSize;
    this.loadUserActivities(true);
  }

  private resetPagination(): void {
    this.skipCount = 0;
    this.userActivities = [];
  }

  updateSearch(searchState: SearchState): void {
    if (this.searchText === searchState.value) {
      return;
    }

    this.searchText = searchState.value;
    this.resetPagination();
    this.loadUserActivities();
  }

  toggleFilters(): void {
    const dialogRef = this.dialog.open(FiltersDialogComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        filters: this.filters,
        preselectedValues: this.getAppliedFiltersAsArray(),
        isDisableFilters: this.isDisabled,
      },
    });

    const sub = dialogRef.componentInstance.resetRequest.subscribe((result) => {
      this.onFiltersApplied(result);
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onFiltersApplied(result);
      }

      sub.unsubscribe();
    });
  }

  onFiltersApplied(selectedValues: { name: string; values: number[] }[]): void {
    if (selectedValues && selectedValues.length > 0 && selectedValues.some((f) => f.values && f.values.length > 0)) {
      this.filtersApplied = true;
    } else {
      this.onFilterReset();
      return;
    }

    selectedValues.forEach(({ name, values }) => {
      if (name.toLowerCase() === 'activity type') {
        this.filterMap.activityType = values;
      }
    });
    this.resetPagination();
    this.loadUserActivities();
  }

  onFilterReset(): void {
    const { startDate, endDate } = this.filterMap;

    this.filterMap = {
      startDate: startDate,
      endDate: endDate,
    };

    this.filtersApplied = false;
    this.resetPagination();
    this.loadUserActivities();
  }

  getAppliedFiltersAsArray(): { name: string; values: number[] }[] {
    const applied: { name: string; values: number[] }[] = [];
    if (this.filterMap.activityType?.length) {
      applied.push({
        name: 'Activity Type',
        values: this.filterMap.activityType,
      });
    }
    return applied;
  }

  onPresetPeriod(preset: string): void {
    const range = this.datePickerService.presetPeriod(preset as PresetPeriod);
    this.selectedPeriod = this.datePickerService.selectedPeriod;
    this.filterMap.startDate = range.start.toISOString();
    this.filterMap.endDate = range.end.toISOString();

    this.resetPagination();
    this.loadUserActivities();
  }

  onDateRangeSelected(range: RangeOfDate): void {
    const newRange = this.datePickerService.dateRangeSelected(range);
    this.selectedPeriod = this.datePickerService.selectedPeriod;
    this.filterMap.startDate = newRange.start.toISOString();
    this.filterMap.endDate = newRange.end.toISOString();

    this.resetPagination();
    this.loadUserActivities();
  }

  openExport(): void {
    this.dialog.open<ExportComponent, ExportDialogData>(ExportComponent, {
      autoFocus: false,
      disableClose: false,
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'custom-dialog-container',
      data: {
        tabName: 'userActivity',
        logFilter: this.filterMap,
      },
    });
  }
  get isDisabled(): boolean {
    if (this.isFirstLoad) {
      return this.isLoading || this.initialLoading
    }

    return this.isDisableFilters;
  }

  get exportButtonState(): 'disabled' | 'default' {
    return !this.hasExportData ? 'disabled' : 'default';
  }
  get hasUserActivities(): boolean {
    return this.userActivities && this.userActivities.length > 0;
  }

  get hasMoreUserActivities(): boolean {
    return this.userActivities.length < this.totalCount;
  }

  get sidebarOpen(): boolean {
    return this.merchantService.sidebarOpenSignal();
  }
}
